import React, { useEffect } from "react";
import { Box, CircularProgress, Typography, Card } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ContextHolder, useAuth } from "@frontegg/react";
import { Stack } from "components/layout/reusableLayouts";
import { useBrandingContext } from "context/BrandingContext";
import { useColorScheme } from "context/ColorSchemeContext";
import ActivateCardForm from "components/common/cards/ActivateCardForm";

const ACTIVATE_PATH = "/activate";
const SESSION_CLEARED_KEY = "ACTIVATE_SESSION_CLEARED";

/** Ends the session and returns here signed out. Redirects through
 * `/oauth/logout` rather than the SDK's `logout()`, which leaves the OAuth
 * session alive; `post_logout_redirect_uri` matches `/logout`'s, so no new
 * allowed-redirect entries are needed. */
const clearSessionAndReturn = () => {
  const baseUrl = ContextHolder.getContext().baseUrl;
  localStorage.setItem("_REDIRECT_AFTER_LOGIN_", ACTIVATE_PATH);
  localStorage.removeItem("isSetup");
  window.location.replace(
    `${baseUrl}/oauth/logout?post_logout_redirect_uri=${process.env.REACT_APP_URL}`,
  );
};

const readSessionCleared = () => {
  try {
    return sessionStorage.getItem(SESSION_CLEARED_KEY) === "1";
  } catch {
    return true;
  }
};

const writeSessionCleared = () => {
  try {
    sessionStorage.setItem(SESSION_CLEARED_KEY, "1");
    return true;
  } catch {
    return false;
  }
};

const ActivatePhysicalCardContent: React.FC = () => {
  const { t } = useTranslation();
  const { logos, platform } = useBrandingContext();
  const { isDark } = useColorScheme();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated) {
      if (readSessionCleared()) return;
      if (writeSessionCleared()) clearSessionAndReturn();
      return;
    }

    writeSessionCleared();
    localStorage.setItem("_REDIRECT_AFTER_LOGIN_", ACTIVATE_PATH);
    navigate("/login");
  }, [isLoading, isAuthenticated, navigate]);

  const handleActivated = () => {
    try {
      sessionStorage.removeItem(SESSION_CLEARED_KEY);
    } catch {
      // Nothing to release if the store is unavailable.
    }
  };

  const handleSignOut = () => {
    writeSessionCleared();
    clearSessionAndReturn();
  };

  const signedInName = user?.name || user?.email || undefined;
  const willClearSession = isAuthenticated && !readSessionCleared();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--space-9)",
        padding: "var(--space-7)",
        backgroundColor: "var(--color-bg-page)",
        backgroundImage:
          "radial-gradient(ellipse 90% 55% at 50% 0%, var(--color-brand-12), transparent 70%)",
      }}
    >
      <Box
        component="img"
        src={isDark ? logos.lightLogo : logos.logo}
        alt={platform?.name || "Outpave"}
        sx={{ height: "auto", maxWidth: "300px", objectFit: "contain" }}
      />

      <Card
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          width: "100%",
          maxWidth: "480px",
          padding: "var(--space-9)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <Stack gap="var(--space-6)">
          <Typography variant="h3" component="h1" sx={{ textAlign: "center" }}>
            {t("Activate Card")}
          </Typography>

          {isLoading || !isAuthenticated || willClearSession ? (
            <Stack sx={{ alignItems: "center", padding: "var(--space-9)" }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <ActivateCardForm
              signedInName={signedInName}
              onSignOut={handleSignOut}
              onActivated={handleActivated}
            />
          )}
        </Stack>
      </Card>
    </Box>
  );
};

const ActivatePhysicalCard: React.FC = () => <ActivatePhysicalCardContent />;

export default ActivatePhysicalCard;
